/**
 * EnvelopeModule — ADSR, driving an AudioParam via a ConstantSourceNode.
 *
 * The ConstantSourceNode emits a single DC offset value (0..1) that the
 * envelope shapes over time. Connect `modOutput` to any target AudioParam
 * (typically VCAModule.modIn('gain') or FilterModule.cutoff's AudioParam).
 * AudioParam connections sum with the param's base value, so the envelope
 * adds on top of whatever the knob is set to.
 *
 * Scheduling:
 *   attack  — linearRampToValueAtTime(1, now + a)
 *   decay   — setTargetAtTime(sustain, now + a, decay / 3)
 *   release — cancel future ramps, setTargetAtTime(0, now, release / 3)
 *
 * The /3 comes from setTargetAtTime's time-constant convention (the value
 * approaches the target by ~63% per time-constant; three constants ≈ 95%).
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

export class EnvelopeModule implements SynthModule {
  readonly kind = 'modulation' as const;
  readonly type = 'envelope' as const;
  readonly displayName = 'ENV';
  readonly id: string;
  bypass = false;

  // Attack 1 ms → 4 s covers the standard synth envelope territory from
  // "snap" through "pluck" to "long swell". The exp curve places the
  // midpoint at ~63 ms, so the first quarter of the knob covers 1–8 ms
  // (percussive), the middle half covers 10 ms–500 ms (musical), and the
  // top quarter covers 500 ms–4 s (pads). Default is 5 ms — fast but not
  // click-fast, so moving the knob up produces an immediately audible swell.
  readonly attack = new NumericParameter({
    name: 'Attack',
    default: 0.005,
    min: 0.001,
    max: 4,
    curve: 'exp',
    unit: 's',
    smoothingMs: 0, // ADSR values read at gate time; smoothing doesn't apply
    // Bias short — a 4-second random attack on every dice-roll is tiresome.
    randomSkew: 0.5,
    formatter: formatTime,
  });
  readonly decay = new NumericParameter({
    name: 'Decay',
    default: 0.15,
    min: 0.001,
    max: 4,
    curve: 'exp',
    unit: 's',
    smoothingMs: 0,
    // Mild short bias.
    randomSkew: 0.7,
    formatter: formatTime,
  });
  readonly sustain = new NumericParameter({
    name: 'Sustain',
    default: 0.7,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 0,
    // Bias high — low sustain + fast decay silences the held note.
    randomSkew: 2,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  readonly release = new NumericParameter({
    name: 'Release',
    default: 0.25,
    min: 0.001,
    max: 4,
    curve: 'exp',
    unit: 's',
    smoothingMs: 0,
    randomSkew: 0.7,
    formatter: formatTime,
  });

  readonly parameters: readonly AnyParameter[] = [
    this.attack,
    this.decay,
    this.sustain,
    this.release,
  ];

  /** Modulation modules have no audio I/O. */
  readonly input: AudioNode | null = null;
  readonly output: AudioNode | null = null;

  private source: ConstantSourceNode | null = null;
  private engine: AudioEngine | null = null;
  private started = false;

  constructor(id?: string) {
    this.id = id ?? mintId('env');
  }

  get modOutput(): AudioNode | null {
    return this.source;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    this.source = engine.context.createConstantSource();
    this.source.offset.value = 0;
    this.source.start();
    this.started = true;
  }

  /**
   * Open/close the envelope gate. Opens: attack→decay→sustain. Closes:
   * release from whatever the current value is.
   */
  gate(open: boolean, at?: number): void {
    if (!this.engine || !this.source) return;
    const ctx = this.engine.context;
    const when = Math.max(at ?? ctx.currentTime, ctx.currentTime);
    const offset = this.source.offset;
    const current = offset.value;

    offset.cancelScheduledValues(when);

    if (open) {
      const a = Math.max(0.001, this.attack.value);
      const d = Math.max(0.001, this.decay.value);
      const s = Math.max(0, Math.min(1, this.sustain.value));

      // Anchor the current value at the gate time so the attack starts from
      // wherever we are (handles retriggering during release gracefully).
      offset.setValueAtTime(current, when);
      offset.linearRampToValueAtTime(1, when + a);
      // Decay — exponential approach to sustain. setTargetAtTime uses a
      // time-constant, not a duration; /3 gives ~95% settle by `d` seconds.
      offset.setTargetAtTime(s, when + a, d / 3);
    } else {
      const r = Math.max(0.001, this.release.value);
      offset.setValueAtTime(current, when);
      offset.setTargetAtTime(0, when, r / 3);
    }
  }

  dispose(): void {
    if (this.source) {
      try {
        this.source.stop();
      } catch {
        // already stopped
      }
      this.source.disconnect();
      this.source = null;
    }
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }
}

/** Pretty-print seconds with sensible precision across the 1ms–8s range. */
function formatTime(v: number): string {
  if (v < 0.1) return `${(v * 1000).toFixed(0)}ms`;
  if (v < 1) return `${(v * 1000).toFixed(0)}ms`;
  return `${v.toFixed(2)}s`;
}
